import type { IData } from '../model/IData'
import type { IPerson } from '../model/IPerson'
import type { IQuizQuestion } from '../model/IQuizQuestion'
import { Relationship } from '../model/Relationship'
import { expectDefined } from './expectDefined'
import { isDead } from './isDead'

export enum QuizDifficulty {
	ParditkaAlive,
	ParditkaAndCloseRelativesAlive,
	ParditkaAndCloseRelativesAll,
}

export function makeQuizQuestions(
	data: IData,
	personIdsWithImages: string[],
	difficulty: QuizDifficulty,
) {
	const result: IQuizQuestion[] = []
	const relationships = new Set(
		difficulty === QuizDifficulty.ParditkaAlive
			? [Relationship.Parditka]
			: [
					Relationship.Parditka,
					Relationship.Parent,
					Relationship.Spouse,
					Relationship.Child,
					Relationship.Descendant,
				],
	)
	for (const person of Object.values(data.personsById)) {
		if (
			person.relationship != null &&
			relationships.has(person.relationship) &&
			person.lastName &&
			person.birthDate &&
			(difficulty === QuizDifficulty.ParditkaAndCloseRelativesAll ||
				!isDead(data, person)) &&
			personIdsWithImages.includes(person.id)
		) {
			try {
				result.push({
					personId: person.id,
					imageIndex: -1,
					choicePersonIds: getChoicePersonIds(data, person, 5, difficulty),
				})
			} catch (e) {
				console.error(e)
			}
		}
	}
	return pick(Infinity, result)
}

function getChoicePersonIds(
	data: IData,
	person: IPerson,
	count: number,
	difficulty: QuizDifficulty,
) {
	const birthYear = expectDefined(getBirthYear(person))
	const names = new Set([
		`${person.honorific} ${person.firstName} ${person.lastName}`,
	])
	const similarPersonIds: string[] = []
	const ageDiff20: IPerson[] = []
	const ageDiffAll: IPerson[] = []
	for (const otherPerson of Object.values(data.personsById)) {
		const name = `${otherPerson.honorific} ${otherPerson.firstName} ${otherPerson.lastName}`
		if (
			otherPerson.id !== person.id &&
			otherPerson.isMale === person.isMale &&
			otherPerson.firstName &&
			!names.has(name) &&
			otherPerson.birthDate
		) {
			if (difficulty === QuizDifficulty.ParditkaAlive) {
				if (
					otherPerson.relationship !== Relationship.Parditka ||
					isDead(data, otherPerson)
				)
					continue
			} else if (difficulty === QuizDifficulty.ParditkaAndCloseRelativesAlive) {
				if (
					otherPerson.relationship === Relationship.Ancestor ||
					otherPerson.relationship === Relationship.Related ||
					isDead(data, otherPerson)
				)
					continue
			} else if (difficulty === QuizDifficulty.ParditkaAndCloseRelativesAll) {
				if (
					otherPerson.relationship === Relationship.Ancestor ||
					otherPerson.relationship === Relationship.Related
				)
					continue
			}
			const ageDiff = Math.abs(
				birthYear - expectDefined(getBirthYear(otherPerson)),
			)
			if (ageDiff <= 10) {
				similarPersonIds.push(otherPerson.id)
				names.add(name)
			} else if (ageDiff <= 20) {
				ageDiff20.push(otherPerson)
			} else {
				ageDiffAll.push(otherPerson)
			}
		}
	}
	if (similarPersonIds.length < count - 1) {
		for (const otherPerson of ageDiff20) {
			if (!names.has(otherPerson.firstName!)) {
				similarPersonIds.push(otherPerson.id)
				names.add(otherPerson.firstName!)
			}
		}
	}
	if (similarPersonIds.length < count - 1) {
		for (const otherPerson of ageDiffAll) {
			if (!names.has(otherPerson.firstName!)) {
				similarPersonIds.push(otherPerson.id)
				names.add(otherPerson.firstName!)
			}
		}
	}
	return insertAtRandom(person.id, pick(count - 1, similarPersonIds))
}

function pick<T>(count: number, array: T[]): T[] {
	if (count > array.length) count = array.length
	const result: T[] = []
	for (let i = 0; i < count; i++) {
		const index = Math.floor(Math.random() * array.length)
		result.push(...array.splice(index, 1))
	}
	return result
}

function insertAtRandom<T>(element: T, array: T[]) {
	const index = Math.floor(Math.random() * (array.length + 1))
	array.splice(index, 0, element)
	return array
}

function getBirthYear(person: IPerson) {
	if (!person.birthDate) return undefined
	return parseInt(person.birthDate.slice(0, 4), 10)
}
